<template>
    <form v-on:submit.prevent="saveForm" novalidate>
        <XIcon
            class="h-8 w-8 text-blue-900 hover:text-gray-400 absolute cursor-pointer right-0 top-0 m-4 p-4 -mt-2 mr-3"
            @click="closeModal()"
        />
        <section :class="{ 'opacity-30': form.delete }">
            <section v-if="edit.hash">
                <label class="block text-sm font-medium text-gray-700 -mt-1">{{
                    t("meeting-form.title", "Titel")
                }}</label>
                <div class="mt-1 relative rounded-md shadow-sm">
                    <input
                        type="text"
                        class="block w-full pr-10 focus:outline-none sm:text-sm rounded-md"
                        :class="{
                            'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                getError('title'),
                        }"
                        @keypress="
                            errors = null;
                            form_error = null;
                        "
                        v-model="form.title"
                    />
                    <div
                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                    >
                        <ExclamationCircleIcon
                            v-if="getError('title')"
                            class="h-5 w-5 text-red-500"
                            aria-hidden="true"
                        />
                    </div>
                </div>
                <p class="mt-2 text-xs text-red-600" v-if="getError('title')">
                    {{ getError("title", true) }}
                </p>
            </section>
            <div class="mt-2">
                <label
                    for="date"
                    class="block text-sm font-medium leading-5 text-gray-700"
                    >{{ t("meeting-form.date", "Datum") }}</label
                >
                <div class="mt-1 relative rounded-md shadow-sm">
                    <div
                        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                    >
                        <svg
                            class="h-5 w-5 text-gray-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                clip-rule="evenodd"
                                fill-rule="evenodd"
                            />
                        </svg>
                    </div>
                    <input
                        id="date"
                        type="tel"
                        v-maska="'##-##-####'"
                        v-model="form.form_date"
                        placeholder="dd-mm-jjjj"
                        class="block w-full px-10 focus:outline-none sm:text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        @keypress="
                            errors = null;
                            form_error = null;
                        "
                        :disabled="form.status == 'past'"
                        :class="{
                            'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                form_error == 'date' || getError('date'),
                        }"
                    />
                    <div
                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                    >
                        <ExclamationCircleIcon
                            v-if="form_error == 'date'"
                            class="h-5 w-5 text-red-500"
                            aria-hidden="true"
                        />
                    </div>
                </div>
                <p
                    class="mt-2 text-xs text-red-600"
                    v-if="form_error == 'date'"
                >
                    <span
                        v-html="
                            t(
                                'meeting-form.date-format',
                                'De datum moet van het formaat<kbd>dd-mm-jjjj</kbd> zijn en in de toekomst liggen'
                            )
                        "
                    ></span>
                </p>
            </div>

            <div class="mt-2 mb-2">
                <label
                    for="time"
                    class="block text-sm font-medium leading-5 text-gray-700"
                    >{{ t("meeting-form.time", "Tijdstip") }}</label
                >
                <div class="mt-1 relative rounded-md shadow-sm">
                    <div
                        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                    >
                        <svg
                            class="h-5 w-5 text-gray-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                clip-rule="evenodd"
                                fill-rule="evenodd"
                            />
                        </svg>
                    </div>
                    <input
                        id="time"
                        type="tel"
                        class="block w-full px-10 focus:outline-none sm:text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="form.status == 'past'"
                        :class="{
                            'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                form_error == 'time' || getError('time'),
                        }"
                        v-maska="'##:##'"
                        v-model="form.time"
                        placeholder="00:00"
                        @keypress="
                            errors = null;
                            form_error = null;
                        "
                    />
                    <div
                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                    >
                        <ExclamationCircleIcon
                            v-if="form_error == 'time' || getError('time')"
                            class="h-5 w-5 text-red-500"
                            aria-hidden="true"
                        />
                    </div>
                </div>
                <p
                    class="mt-2 text-xs text-red-600"
                    v-if="form_error == 'time'"
                >
                    <span
                        v-html="
                            t(
                                'meeting-form.time-format',
                                'Het tijdstip moet genoteerd worden in <kbd>23:59</kbd>-formaat en het tijdstip moet valide zijn.'
                            )
                        "
                    ></span>
                </p>
            </div>
            <section v-if="options">
                <label class="block text-sm font-medium text-gray-700 mb-2">{{
                    t("form-meeting.role_id", "Genodigden")
                }}</label>
                <div class="mt-1 relative rounded-md shadow-sm mb-6">
                    <select
                        v-model="form.role_id"
                        class="block w-full pl-3 pr-10 py-2 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        :class="{
                            'disabled pointer-events-none bg-gray-100':
                                form.status == 'past',
                        }"
                    >
                        <option value="">
                            {{ t("filter.role.all_members", "Alle leden") }}
                        </option>
                        <optgroup
                            :label="t('filter.' + optgroup)"
                            v-for="(options, optgroup) in options"
                            v-bind:key="optgroup"
                        >
                            <option
                                v-for="(option, value) in options"
                                v-bind:key="value"
                                :value="value"
                            >
                                {{ option }}
                            </option>
                        </optgroup>
                    </select>
                </div>
            </section>

            <section
                v-if="form.id && form.show_embed"
                class="bg-gray-50 overflow-hidden shadow rounded-lg mt-4"
            >
                <div class="px-5 py-2">
                    <!-- PHONE -->
                    <label class="block text-sm font-medium text-gray-700"
                        >Embed-code</label
                    >
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <textarea
                            class="block w-full pr-10 focus:outline-none rounded-md font-mono text-xs"
                            :rows="form.embed && form.embed.length > 50 ? 9 : 2"
                            :class="{
                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                    getError('embed'),
                            }"
                            @keypress="errors = null"
                            v-model="form.embed"
                        />
                        <div
                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                        >
                            <ExclamationCircleIcon
                                v-if="getError('embed')"
                                class="h-5 w-5 text-red-500"
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                    <p class="mt-2 text-xs text-gray-600">
                        {{
                            t(
                                "meeting-form.embed-helper",
                                "Plaats hier de embed-code van Vimeo of YouTube. Wanneer dit wordt leeggelaten, zal de vergadering met de standaard werking zijn van Online-ALV.nl"
                            )
                        }}
                    </p>
                </div>
            </section>
        </section>
        <SwitchGroup
            as="div"
            class="flex items-center mt-4"
            v-show="edit.id"
            @click="checkDelete()"
        >
            <Switch
                v-model="form.delete"
                :class="[
                    form.delete ? 'bg-red-600' : 'bg-gray-200',
                    'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500',
                ]"
            >
                <span
                    :class="[
                        form.delete ? 'translate-x-5' : 'translate-x-0',
                        'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                    ]"
                >
                    <span
                        :class="[
                            form.delete
                                ? 'opacity-0 ease-out duration-100'
                                : 'opacity-100 ease-in duration-200',
                            'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                        ]"
                        aria-hidden="true"
                    >
                        <svg
                            class="h-3 w-3 text-gray-400"
                            fill="none"
                            viewBox="0 0 12 12"
                        >
                            <path
                                d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                    <span
                        :class="[
                            form.delete
                                ? 'opacity-100 ease-in duration-200'
                                : 'opacity-0 ease-out duration-100',
                            'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                        ]"
                        aria-hidden="true"
                    >
                        <svg
                            class="h-3 w-3 text-red-600"
                            fill="currentColor"
                            viewBox="0 0 12 12"
                        >
                            <path
                                d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                            />
                        </svg>
                    </span>
                </span>
            </Switch>

            <SwitchLabel as="span" class="ml-3">
                <span class="text-sm font-medium text-gray-900">{{
                    t("meeting-form.delete", "Verwijder vergadering")
                }}</span>
                <span class="text-xs text-red-700 block" v-if="form.delete">{{
                    t("meeting-form.delete-warning", "inclusief alle historie!")
                }}</span>
            </SwitchLabel>
        </SwitchGroup>

        <div class="mt-3 sm:mt-6">
            <button
                type="submit"
                class="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:opacity-50"
                :class="{
                    'bg-red-600 text-red-50 hover:bg-red-700': form.delete,
                    'bg-blue-600 text-blue-50 hover:bg-blue-700': !form.delete,
                }"
                :disabled="loading"
            >
                <svg
                    v-if="loading"
                    class="animate-spin h-5 w-5 mr-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                </svg>

                {{ t("save", "Opslaan") }}
            </button>
        </div>
    </form>
</template>

<script>
import { ExclamationCircleIcon, XIcon } from "@heroicons/vue/solid";
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
export default {
    components: {
        ExclamationCircleIcon,
        Switch,
        SwitchGroup,
        SwitchLabel,
        XIcon,
        // VueMaskDirective,
    },
    mounted() {
        if (this.edit) {
            this.form = this.edit;
        }
        Eventing.fire("formMeeting");
    },
    created() {
        this.getFilterOptions();
    },
    props: ["edit"],
    data() {
        return {
            form: {
                form_date: null,
                title: "Vergadering",
                time: null,
            },
            loading: false,
            errors: null,
            form_error: null,
            options: null,
        };
    },
    methods: {
        getFilterOptions() {
            axios
                .get("/api/modules/roles?options=role_id")
                .then((response) => {
                    this.options = response.data;
                })
                .catch((error) => {});
        },
        closeModal() {
            Eventing.fire("closeModal");
        },
        getError(error, text) {
            try {
                if (this.errors) {
                    let errors = Object.values(this.errors);
                    if (text) {
                        return this.errors[error].toString();
                    }
                    return this.errors[error].toString() ? true : false;
                }
            } catch (error) {
                return false;
            }
            return false;
        },
        saveForm() {
            let self = this;
            this.loading = true;
            var url = "/api/meetings";
            var method = "post";
            if (this.edit.hash && this.edit.hash != "undefined") {
                url = "/api/meetings/" + this.edit.hash;
                method = "put";
            }
            axios({
                method: method,
                url: url,
                data: this.form,
            })
                .then((response) => {
                    this.loading = false;
                    Eventing.fire("fetchMeetings");
                    if (response.data.data.status == "past") {
                        this.$router.push({ name: "meetings-history" });
                    } else {
                        this.$router.push({ name: "meetings-index" });
                    }
                })
                .catch((error) => {
                    if (error.response) {
                        if (error.response.status == 404) {
                            this.$router.push("/login");
                        }
                        if (error.response.status == 409) {
                            self.form_error = "time";
                        }
                        if (error.response.status == 422) {
                            self.form_error = "date";
                            this.errors = error.response.data.errors;
                        }
                    }
                    this.loading = false;
                });
        },
        checkDelete() {
            var val = this.form.delete;
            if (val == true) {
                Eventing.fire("warning");
            } else {
                Eventing.fire("noWarning");
            }
        },
    },
};
</script>
